@layer refine.base {
  :host {
    --_font-size: var(--file-input-font-size, var(--text-sm));
    --_radius: var(--file-input-radius, var(--rounded-lg));
    --_bg: var(--file-input-bg, var(--color-contrast-100));
    --_border-color: var(--file-input-border-color, var(--color-contrast-300));
    --_min-height: var(--file-input-min-height, var(--size-36));

    display: inline-flex;
    flex-direction: column;
    align-items: stretch;
  }

  .file-input-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--size-1-5);
    width: 100%;
  }

  /* ========================================
       Label
       ======================================== */

  .label-outside {
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    line-height: var(--leading-none);
    color: var(--color-contrast-500);
    cursor: default;
    user-select: none;
    transition: color var(--transition-fast);
  }

  /* ========================================
       Dropzone
       ======================================== */

  .dropzone {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--size-2);
    align-items: center;
    justify-content: center;
    min-height: var(--_min-height);
    padding: var(--size-6) var(--size-4);
    text-align: center;
    cursor: pointer;
    outline: none;
    background: var(--_bg);
    border: var(--border-2) dashed var(--_border-color);
    border-radius: var(--_radius);
    transition:
      background var(--transition-fast),
      border-color var(--transition-fast),
      box-shadow var(--transition-fast);
  }

  .dropzone-content {
    display: flex;
    flex-direction: column;
    gap: var(--size-2);
    align-items: center;
    pointer-events: none;
  }

  .dropzone-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-contrast-400);
    transition:
      color var(--transition-fast),
      transform var(--transition-fast);
  }

  .dropzone-title {
    font-size: var(--_font-size);
    font-weight: var(--font-medium);
    line-height: var(--leading-snug);
    color: var(--color-contrast-700);
  }

  .dropzone-title u {
    color: var(--_theme-focus, var(--color-primary));
    text-decoration: underline;
    text-decoration-color: transparent;
    text-underline-offset: 2px;
    transition: text-decoration-color var(--transition-fast);
  }

  /* Signal 01: the copy itself during an active drag, not just the border/glow. */
  .dropzone-title-active {
    color: var(--_theme-focus, var(--color-primary));
  }

  .dropzone-hint {
    font-size: var(--text-xs);
    line-height: var(--leading-tight);
    color: var(--color-contrast-400);
  }

  /* ========================================
       Helper Text
       ======================================== */

  .helper-text[hidden] {
    display: none;
  }

  .helper-text {
    padding-inline: 2px;
    font-size: var(--text-xs);
    line-height: var(--leading-tight);
    color: color-mix(in oklch, var(--color-contrast-500) 80%, var(--color-canvas));
  }

  /* ========================================
       File List
       ======================================== */

  .file-list {
    display: flex;
    flex-direction: column;
    gap: var(--size-1);
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .file-item {
    box-sizing: border-box;
    display: flex;
    gap: var(--size-3);
    align-items: center;
    padding: var(--size-2) var(--size-3);
    background: var(--color-contrast-50);
    border: var(--border) solid var(--color-contrast-200);
    border-radius: var(--_radius);
    transition: background var(--transition-fast);
  }

  .file-item:hover {
    background: var(--color-contrast-100);
  }

  .file-icon {
    flex-shrink: 0;
    color: var(--_theme-focus, var(--color-primary));
  }

  .file-meta {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--size-0-5);
    min-width: 0;
  }

  .file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-contrast-700);
    white-space: nowrap;
  }

  .file-size {
    font-size: var(--text-xs);
    color: var(--color-contrast-400);
  }

  .file-actions {
    display: flex;
    flex-shrink: 0;
    gap: var(--size-0-5);
  }

  .file-remove,
  .file-action {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: var(--size-1);
    color: var(--color-contrast-400);
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: var(--rounded-sm);
    transition:
      color var(--transition-fast),
      background var(--transition-fast);
  }

  .file-remove:hover {
    color: var(--color-error);
    background: var(--color-error-backdrop, color-mix(in oklch, var(--color-error) 12%, transparent));
  }

  /* Signal 03/04: retry and replace share one neutral-hover treatment — distinct from remove's
     error-red hover so the destructive action doesn't blend in with the recovery ones. */
  .file-action:hover {
    color: var(--_theme-focus, var(--color-primary));
    background: var(--_theme-backdrop, color-mix(in oklch, var(--color-primary) 12%, transparent));
  }

  /* ========================================
       Upload Status (Signals 02, 03, 04)
       ======================================== */

  .file-progress-meta {
    font-size: var(--text-xs);
    color: var(--color-contrast-400);
  }

  .file-error-text {
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: var(--text-xs);
    color: var(--color-error);
    white-space: nowrap;
  }

  .file-item[data-status='success'] .file-icon {
    color: var(--color-success);
  }

  .file-item[data-status='error'] .file-icon {
    color: var(--color-error);
  }

  .file-item[data-status='error'] {
    border-color: color-mix(in oklch, var(--color-error) 40%, var(--color-contrast-200));
  }

  /* ========================================
       File Gallery (preview grid)
       ======================================== */

  .file-grid {
    --_thumb-size: var(--file-input-thumb-size, var(--size-24));

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--_thumb-size), 1fr));
    gap: var(--size-2);
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .file-card {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--size-1);
  }

  .file-thumb-frame {
    position: relative;
    display: block;
  }

  .file-thumb {
    box-sizing: border-box;
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    background: var(--color-contrast-100);
    border: var(--border) solid var(--color-contrast-200);
    border-radius: var(--_radius);
  }

  .file-thumb-generic {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-contrast-400);
  }

  .file-card[data-status='uploading'] .file-thumb {
    opacity: 0.6;
  }

  .file-card[data-status='error'] .file-thumb {
    border-color: color-mix(in oklch, var(--color-error) 50%, var(--color-contrast-200));
  }

  .file-card-name {
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: var(--text-xs);
    color: var(--color-contrast-600);
    text-align: center;
    white-space: nowrap;
  }

  /* Signal 04: a small badge over the thumbnail corner — an explicit success/error indicator
     next to the visual proof itself, not just a caption line below it. */
  .file-status-badge {
    position: absolute;
    top: var(--size-1);
    left: var(--size-1);
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--size-5);
    height: var(--size-5);
    color: var(--color-canvas);
    border-radius: var(--rounded-full);
  }

  .file-status-badge-success {
    background: var(--color-success);
  }

  .file-status-badge-error {
    background: var(--color-error);
  }

  .file-card-actions {
    position: absolute;
    top: var(--size-1);
    right: var(--size-1);
    display: flex;
    gap: var(--size-0-5);
  }

  .file-card-action {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: var(--size-5);
    height: var(--size-5);
    padding: 0;
    color: var(--color-contrast-50);
    cursor: pointer;
    background: color-mix(in oklch, var(--color-contrast-900) 55%, transparent);
    border: none;
    border-radius: var(--rounded-full);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  .file-card-remove:hover {
    color: var(--color-canvas);
    background: var(--color-error);
  }

  .file-card-action:not(.file-card-remove):hover {
    color: var(--color-canvas);
    background: var(--color-primary);
  }

  .file-card .file-progress-meta,
  .file-card .file-error-text {
    text-align: center;
    white-space: normal;
  }

  /* ========================================
       Hover & Focus
       ======================================== */

  :host(:not([disabled])) .dropzone:hover {
    border-color: var(--color-contrast-400);
  }

  :host(:not([disabled])) .dropzone:hover .dropzone-title u {
    text-decoration-color: var(--_theme-focus, var(--color-primary));
  }

  :host(:not([disabled])) .dropzone:focus-visible {
    border-color: var(--_theme-focus, var(--color-primary));
    box-shadow: var(--_theme-shadow, var(--color-primary-focus-shadow));
  }

  :host(:not([disabled])) .dropzone:hover .dropzone-icon,
  :host(:not([disabled])) .dropzone:focus-visible .dropzone-icon {
    color: var(--_theme-focus, var(--color-primary));
    transform: translateY(-1px);
  }

  /* ========================================
       Drag-Over State
       ======================================== */

  :host([drag-over]) .dropzone {
    background: color-mix(in oklch, var(--_theme-base, var(--color-primary)) 8%, transparent);
    border-color: var(--_theme-focus, var(--color-primary));
    border-style: solid;

    /* A genuine glow (not the same subtle ring `:focus-visible` uses above) — a soft inner
       ring plus a wider, brighter halo, so an active drag reads unmistakably differently from
       a merely-focused, idle dropzone. */
    box-shadow:
      0 0 0 3px color-mix(in oklch, var(--_theme-focus, var(--color-primary)) 25%, transparent),
      0 0 20px 2px color-mix(in oklch, var(--_theme-focus, var(--color-primary)) 45%, transparent);
  }

  :host([drag-over]) .dropzone .dropzone-icon {
    color: var(--_theme-focus, var(--color-primary));
    transform: translateY(-2px) scale(1.05);
  }

  /* ========================================
       Error State
       ======================================== */

  :host([invalid]) .dropzone {
    border-color: var(--color-error);
  }

  :host([invalid]) .label-outside {
    color: var(--color-error);
  }

  /* Signal 03: an in-field icon alongside the border shift — never color alone. */
  .dropzone-icon[data-status='error'] {
    color: var(--color-error);
  }

  /* ========================================
       Error Text
       ======================================== */

  .helper-text-error {
    color: var(--color-error);
  }
}

@layer refine.variants {
  /* Solid (Default) */
  :host(:not([variant])),
  :host([variant='solid']) {
    --_bg: var(--file-input-bg, var(--color-contrast-50));
    --_border-color: var(--file-input-border-color, var(--color-contrast-300));
  }

  /* Flat — a near-invisible border, deliberately weaker than bordered's
     70%-focus border (matches button.css's flat/bordered split) so the two
     don't read as the same box at a glance. */
  :host([variant='flat']) {
    --_bg: var(--file-input-bg, var(--color-contrast-100));
    --_border-color: var(--file-input-border-color, color-mix(in oklch, var(--_theme-focus) 20%, transparent));
  }

  :host([variant='flat']) .dropzone:hover {
    background: var(--file-input-hover-bg, color-mix(in oklch, var(--_theme-base) 6%, var(--color-contrast-100)));
    border-color: var(
      --file-input-hover-border-color,
      color-mix(in oklch, var(--_theme-base) 35%, var(--color-contrast-300))
    );
  }

  :host([variant='flat']) .dropzone:focus-visible,
  :host([variant='flat'][drag-over]) .dropzone {
    background: var(--file-input-focus-bg, color-mix(in oklch, var(--_theme-base) 8%, var(--color-canvas)));
    border-color: var(--file-input-focus-border-color, color-mix(in oklch, var(--_theme-focus) 60%, transparent));
    box-shadow: var(--_theme-shadow);
  }

  /* Bordered */
  :host([variant='bordered']) {
    --_bg: var(--file-input-bg, var(--_theme-backdrop));
    --_border-color: var(--file-input-border-color, color-mix(in oklch, var(--_theme-focus) 70%, transparent));
  }

  :host([variant='bordered']) .dropzone:hover {
    border-color: var(--file-input-hover-border-color, var(--_theme-focus));
  }

  /* Outline */
  :host([variant='outline']) {
    --_bg: var(--file-input-bg, transparent);
  }

  /* Ghost */

  /* Borderless at rest — only reveals a border on hover (the border-*width* stays reserved via
     a transparent color, not `none`, so revealing it on hover never shifts layout). Focus
     already gets its own themed border + glow from the generic `:focus-visible` rule above,
     regardless of variant, so it needs no ghost-specific override here. */
  :host([variant='ghost']) {
    --_bg: var(--file-input-bg, transparent);
    --_border-color: var(--file-input-border-color, transparent);
  }

  :host(:not([disabled])[variant='ghost']) .dropzone:hover {
    background: var(--file-input-hover-bg, var(--color-contrast-100));
    border-color: var(--file-input-hover-border-color, var(--color-contrast-200));
  }
}

@layer refine.utilities {
  :host([fullwidth]) {
    width: 100%;
  }
}

/* ========================================
     Disabled State — intentionally unlayered
     ======================================== */

/*
 * The shared `disabledLoadingMixin` (pulled in via `fieldMixins`) dims `:host([disabled])`
 * with `opacity: 0.5` as bare, unlayered CSS. Cascade layers always lose to unlayered rules
 * regardless of source order or specificity, so overriding that from inside any `@layer`
 * block in this file (including `refine.overrides`) would be a no-op — this has to stay
 * unlayered too, to actually win. See `ore-input`'s `input.css` for the same pattern.
 *
 * Per the field-state UX review: opacity-based dimming reads as a washed-out/loading look
 * and can mimic reduced-contrast content for low-vision users, rather than a clearly locked
 * field. Swapped for an explicit grayscale surface + `not-allowed` cursor (the cursor already
 * comes from the shared mixin, left untouched here).
 */
:host([disabled]) {
  opacity: 1;
}

:host([disabled]) .dropzone {
  background: var(--color-contrast-100);
  border-color: var(--color-contrast-200);
}

:host([disabled]) .dropzone-icon,
:host([disabled]) .dropzone-title,
:host([disabled]) .dropzone-hint,
:host([disabled]) .dropzone-title u {
  color: var(--color-contrast-400);
}

:host([disabled]) .label-outside {
  color: var(--color-contrast-400);
}
